/**
 * The API surface the app codes against.
 *
 * Two implementations satisfy it: `http.ts` (real Django backend) and
 * `mock.ts` (in-memory, for development before Phase 2 lands). Components and
 * stores only ever see this interface, so swapping adapters is one line in
 * `index.ts` — no component changes.
 */
import type {
  Assignment, AttendanceRecord, BoardState, Certificate, Course, Module, Page, Payout,
  Question, Rating, ShiftPost, Site, Timesheet, VettingStage, Worker,
  WorkerDocument, WorkerSkill, DocType,
  OnboardingStatus, ScreeningQuestion, OrientationSection, RequiredDocument,
  BankDetails, ReferralSummary,
} from './types'

export interface ShiftFilters {
  from?: string
  to?: string
  skill?: string
  max_distance_km?: number
  cursor?: string
}

export interface PinchcrewApi {
  // ---- auth (phone + OTP; no password) ----
  requestOtp(phone: string): Promise<{ sent_to: string; expires_in: number }>
  verifyOtp(phone: string, code: string): Promise<{ access: string; worker: Worker; is_new: boolean }>
  logout(): Promise<void>
  /** True when a token survives a reload. */
  hasStoredSession(): boolean

  // ---- profile & vetting ----
  getMe(): Promise<Worker>
  updateMe(patch: Partial<Worker>): Promise<Worker>
  getMySkills(): Promise<WorkerSkill[]>
  getMyDocuments(): Promise<WorkerDocument[]>
  uploadDocument(docType: DocType, file: File): Promise<WorkerDocument>
  getVettingStages(): Promise<VettingStage[]>

  // ---- progressive onboarding ----
  getOnboardingStatus(): Promise<OnboardingStatus>
  getQuestionnaire(): Promise<{ questions: ScreeningQuestion[]; answers: Record<string, unknown>; submitted: boolean }>
  submitQuestionnaire(answers: Record<string, unknown>): Promise<{ status: string }>
  getOrientation(): Promise<{ version: string; sections: OrientationSection[]; acknowledged: boolean }>
  acknowledgeOrientation(): Promise<{ acknowledged: boolean; stages_passed: string[] }>
  getRequiredDocuments(): Promise<RequiredDocument[]>
  getBankDetails(): Promise<BankDetails>
  saveBankDetails(input: { bank_name: string; account_no: string; account_holder: string }): Promise<BankDetails>
  getReferrals(): Promise<ReferralSummary>
  useReferralCode(code: string): Promise<{ attached: boolean; referrer_name: string | null; your_bonus: string | null }>

  // ---- shifts: the core loop ----
  browseShifts(filters?: ShiftFilters): Promise<Page<ShiftPost> & { board?: BoardState }>
  getShift(id: string): Promise<ShiftPost>
  claimShift(id: string): Promise<Assignment>
  cancelClaim(assignmentId: string, reason?: string): Promise<void>
  getMyAssignments(scope?: 'upcoming' | 'past'): Promise<Assignment[]>

  // ---- attendance ----
  clockIn(assignmentId: string, qrPayload: string, coords?: GeolocationCoordinates): Promise<AttendanceRecord>
  clockOut(assignmentId: string, qrPayload: string, coords?: GeolocationCoordinates): Promise<AttendanceRecord>
  startBreak(assignmentId: string): Promise<AttendanceRecord>
  endBreak(assignmentId: string): Promise<AttendanceRecord>

  // ---- money ----
  getTimesheets(): Promise<Timesheet[]>
  getPayouts(): Promise<Payout[]>
  disputeTimesheet(id: string, reason: string, claimedMinutes: number): Promise<Timesheet>

  // ---- learning ----
  getCourses(): Promise<Course[]>
  getCourse(slug: string): Promise<{ course: Course; modules: Module[] }>
  completeModule(courseSlug: string, moduleId: string): Promise<Course>
  getQuiz(courseSlug: string): Promise<Question[]>
  submitQuiz(courseSlug: string, answers: Record<string, string>): Promise<{ passed: boolean; score: number; certificate?: Certificate }>
  getCertificates(): Promise<Certificate[]>

  // ---- reputation ----
  getMyRatings(): Promise<Rating[]>
  getSites(): Promise<Site[]>
}

export class ApiRequestError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly fieldErrors?: Record<string, string[]>,
    public readonly status?: number,
  ) {
    super(message)
    this.name = 'ApiRequestError'
  }
}
